import { useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { useTranslate } from 'shared/lib/i18n'

/**
 * «Утилизировать» (ADR-0033, spec §18; ticket 42): the Point's operator disposes of
 * a Parcel its partner decided to dispose of; no Shift. The Parcel becomes
 * «Утилизирована» and the partner is told. It cannot be undone, so it is asked
 * first, in an `AlertDialog`; a refusal is the server's, in words, inside it.
 *
 * Mounted only while asking: closing it — «Отмена», Esc, or the act done — unmounts it.
 * `dispose` is injected so the behaviour is tested without a backend.
 */
export type DisposeRequest = RouterInputs['storage']['Dispose']
export type DisposeAnswer = RouterOutputs['storage']['Dispose']

const disposeViaTrpc = (input: DisposeRequest) => trpcClient.storage.Dispose.mutate(input)

export interface DisposeActProps {
	parcelId: string
	code: string
	dispose?: (input: DisposeRequest) => Promise<DisposeAnswer>
	onDone?: () => void
	onCancel: () => void
}

export function DisposeAct({ parcelId, code, dispose = disposeViaTrpc, onDone, onCancel }: DisposeActProps) {
	const { t } = useTranslate()
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const submit = async () => {
		if (busy) return
		setBusy(true)
		setError(null)
		try {
			const answer = await dispose({ parcelId })
			if (answer.outcome === 'refused') setError(t(`returnRefusal.${answer.reason}`))
			else onDone?.()
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e))
		} finally {
			setBusy(false)
		}
	}

	return (
		<AlertDialog open onOpenChange={(open) => !open && !busy && onCancel()}>
			<AlertDialogContent data-testid='dispose-act'>
				<AlertDialogHeader>
					<AlertDialogTitle>{t('disposal.title', { code })}</AlertDialogTitle>
					<AlertDialogDescription>{t('disposal.body')}</AlertDialogDescription>
				</AlertDialogHeader>
				{error && (
					<p role='alert' className='text-sm text-destructive' data-testid='dispose-error'>
						{error}
					</p>
				)}
				<AlertDialogFooter>
					<AlertDialogCancel disabled={busy}>{t('disposal.cancel')}</AlertDialogCancel>
					<AlertDialogAction
						variant='destructive'
						data-testid='dispose-submit'
						disabled={busy}
						onClick={(event) => {
							// The dialog stays open until the server answers: a refusal is said inside it.
							event.preventDefault()
							void submit()
						}}
					>
						{t('disposal.submit')}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}
